import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore }    from '../store/cartStore.js';
import { usarPermisos } from '../lib/permisos.js';
import { useSessionStore } from '../store/sessionStore.js';
import { useTabsStore }    from '../store/tabsStore.js';
import { useAuthStore }    from '../store/authStore.js';
import { usarPlan } from '../lib/plan.js';
import { useWaiterStore }  from '../store/waiterStore.js';
import WaiterPinSheet     from '../components/WaiterPinSheet.jsx';
import { getProducts, getCategories } from '../api/catalog.api.js';
import CartPanel    from '../components/layout/CartPanel.jsx';
import SyncBadge    from '../components/common/SyncBadge.jsx';
import { useSyncStore } from '../store/syncStore.js';
import NumpadModal  from '../components/NumpadModal.jsx';
import ModifierSheet from '../components/ModifierSheet.jsx';
import MenuSheet from '../components/MenuSheet.jsx';
import { useConfirmacion } from '../components/ConfirmSheet.jsx';
import { cerrarSesion } from '../lib/salir.js';
import { useEsTelefono } from '../lib/viewport.js';
import { disponibleHoy } from '../lib/disponibilidad.js';
import { IcoCheck, IcoPrint, IcoTabs, IcoTables, IcoUser, IcoClipboard, IcoStar, IcoNumpad, IcoStore, IcoSearch, IcoReturn, IcoArrowUp, IcoClose, IcoAparato } from '../components/common/Icons.jsx';
import styles from './CatalogScreen.module.css';

// Lo que se agrega va al comensal seleccionado; sin selección, a la mesa.
const seatsActivos = () => {
  const n = useCartStore.getState().activeSeat;
  return n ? [n] : [];
};

export default function CatalogScreen() {
  const navigate       = useNavigate();
  const { pedir: pedirSalir, Confirmacion: ConfirmarSalir } = useConfirmacion();
  const permisos = usarPermisos();
  const sinEnviar = useSyncStore((s) => s.outboxCount + s.outboxErrors);
  const terminal       = useSessionStore((s) => s.terminal);
  const session        = useSessionStore((s) => s.session);
  const addItem        = useCartStore((s) => s.addItem);
  const cartItems      = useCartStore((s) => s.items);
  const restarProducto        = useCartStore((s) => s.restarProducto);
  const fijarCantidadProducto = useCartStore((s) => s.fijarCantidadProducto);
  const tabCount       = useTabsStore((s) => s.tabs.length);
  const activeTab      = useTabsStore((s) => s.tabs.find((t) => t.id === s.activeTabId) ?? null);
  const nComensales    = useCartStore((s) => s.comensales.length);
  const telefono       = useEsTelefono();
  // En teléfono el buscador vive detrás de la lupa: abierto todo el tiempo se
  // llevaba una fila entera del encabezado en cada toque.
  const [buscando, setBuscando] = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  // Mesas y cocina solo tienen sentido con servicio en mesa. Una tienda no las
  // ve: el backend además las rechaza con 403, esto evita el botón muerto.
  // Son dos preguntas: el giro (¿le aplica?) y el plan (¿lo paga?).
  const plan           = usarPlan();
  const esRestaurante  = plan.servicioEnMesa;

  // El mesero en turno solo tiene sentido con servicio en mesa: en mostrador
  // cobra quien entró con su cuenta y no hay a quién distinguir.
  const waiter     = useWaiterStore((s) => s.waiter);
  const [pinOpen, setPinOpen] = useState(false);

  const [products,       setProducts]       = useState([]);
  const [search,         setSearch]         = useState('');
  const [loading,        setLoading]        = useState(true);
  const [loadErr,        setLoadErr]        = useState(null);
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [scanFlash,      setScanFlash]      = useState(null);
  const [numpadOpen,     setNumpadOpen]     = useState(false);
  const [hasMore,        setHasMore]        = useState(false);
  const [qtyPickerFor,   setQtyPickerFor]   = useState(null);
  const [qtyPickerVal,   setQtyPickerVal]   = useState('');
  const [pinned,         setPinned]         = useState(() => {
    try { return JSON.parse(localStorage.getItem('pos-pinned-products') ?? '[]'); }
    catch { return []; }
  });
  const searchRef = useRef(null);

  const togglePin = (e, product) => {
    e.stopPropagation();
    setPinned((prev) => {
      const exists = prev.find((p) => p._id === product._id);
      const next   = exists
        ? prev.filter((p) => p._id !== product._id)
        : [...prev, { _id: product._id, name: product.name, price: product.price, images: product.images }];
      localStorage.setItem('pos-pinned-products', JSON.stringify(next));
      return next;
    });
  };

  const pinnedFull = pinned
    .map((p) => products.find((pr) => pr._id === p._id) ?? p)
    .filter(Boolean);

  const openQtyPicker = (e, productId, currentQty) => {
    e.stopPropagation();
    setQtyPickerFor(productId);
    setQtyPickerVal(String(currentQty));
  };
  const commitQtyPicker = (productId) => {
    const n = parseInt(qtyPickerVal, 10);
    if (!isNaN(n) && n >= 0) fijarCantidadProducto(productId, n, seatsActivos());
    setQtyPickerFor(null);
  };

  const getProductQty = useCartStore((s) => s.getProductQty);
  const cartQty = (productId) => getProductQty(productId);

  const [modifierFor, setModifierFor] = useState(null);   // producto esperando sus modificadores

  // Los modificadores vienen con el plan: si no los trae, el producto se
  // vende tal cual aunque el catálogo todavía los tenga cargados.
  const hasMods = (product) => plan.modificadores && !!product.modifier_groups?.length;

  // Un producto con modificadores no se puede agregar de un toque: hay que
  // preguntar término, extras, etc. antes de saber su precio final.
  const requestAdd = (product) => {
    if (hasMods(product)) setModifierFor(product);
    else addItem(product, [], 1, 1, seatsActivos());
  };

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
    // En tablet el foco automático despliega el teclado en pantalla y tapa
    // el catálogo. Solo enfocamos donde hay puntero fino (mouse/trackpad),
    // que es también donde el escáner de códigos necesita el foco listo.
    // En táctil quedan F2, escribir para buscar, y tocar el campo.
    if (window.matchMedia('(pointer: fine)').matches) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, []);

  // Keyboard shortcuts: F2 → focus search, Escape → clear search
  useEffect(() => {
    const onKey = (e) => {
      // F2: focus search
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      // Escape: clear search or close numpad
      if (e.key === 'Escape') {
        if (numpadOpen) { setNumpadOpen(false); return; }
        if (search) { setSearch(''); searchRef.current?.focus(); }
        return;
      }
      // Ignore if already in an input
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      // Any printable char → focus search and type.
      // Insertamos la tecla a mano: enfocar no reenvía el evento actual, así
      // que sin esto se perdía el primer carácter de cada código escaneado.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
        setSearch((s) => s + e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search, numpadOpen]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const q = search.trim();
      const result = await getProducts({
        ...(q              && { search: q }),
        ...(activeCategory && { category: activeCategory }),
        // Los insumos de receta son productos, pero no se venden sueltos: no
        // tienen por qué aparecer en el catálogo del mesero.
        sellable: true,
        limit: 200,
      });
      // `total` cuenta también las promos de otros días; el aviso de "hay más"
      // se calcula con lo que llegó, antes de filtrarlas.
      const llegaron = result.data ?? [];
      setProducts(llegaron.filter((p) => disponibleHoy(p)));
      setHasMore(result.total > llegaron.length);
    } catch (err) {
      setLoadErr(err.response?.data?.error ?? err.message ?? 'Error al cargar productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [search, activeCategory]);

  useEffect(() => {
    const t = setTimeout(loadProducts, search ? 150 : 0);
    return () => clearTimeout(t);
  }, [loadProducts, search]);

  // Auto-add when scanner sends Enter and there's exactly 1 result
  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (products.length === 1 && search.trim()) {
      requestAdd(products[0]);
      setScanFlash(products[0].name);
      setSearch('');
      setTimeout(() => setScanFlash(null), 1500);
      // Re-focus so scanner can scan next item immediately
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  };

  return (
    <div className={styles.root}>
      {/* ── Menú "⋯": lo que no se toca cada minuto ───────────────────── */}
      <MenuSheet
        open={menuOpen}
        title={permisos.comandera ? (waiter?.name ?? 'Comandera') : (terminal?.name ?? 'Caja')}
        onClose={() => setMenuOpen(false)}
        items={[
          { label: 'Cuentas abiertas', hint: esRestaurante ? 'Mesas y cuentas por nombre' : 'Ventas guardadas por nombre', icon: <IcoTabs size={18} />, badge: tabCount, onClick: () => navigate('/tabs') },
          esRestaurante && { label: 'Mapa de mesas', icon: <IcoTables size={18} />, onClick: () => navigate('/tables') },
          plan.cocina && { label: 'Pantalla de cocina', hint: 'Comandas en preparación', icon: <IcoStore size={18} />, onClick: () => navigate('/kitchen') },
          esRestaurante && { label: waiter ? `En turno: ${waiter.name}` : 'Identifícate con tu PIN', hint: waiter ? 'Toca para cambiar de mesero' : 'Para que las comandas lleven tu nombre', icon: <IcoUser size={18} />, onClick: () => setPinOpen(true) },
          sinEnviar > 0 && { label: 'Pendientes por subir', hint: 'Órdenes que no han llegado a la caja', icon: <IcoArrowUp size={18} />, badge: sinEnviar, onClick: () => navigate('/pendientes') },
          permisos.devolver && { label: 'Devolución', icon: <IcoReturn size={18} />, onClick: () => navigate('/refund') },
          permisos.cerrarCaja && { label: 'Configurar impresora', icon: <IcoPrint size={18} />, onClick: () => navigate('/printer-setup') },
          permisos.cerrarCaja && { label: 'Historial de sesiones', icon: <IcoClipboard size={18} />, onClick: () => navigate('/sessions-history') },
          { label: 'Este aparato', hint: 'Caja o comandera, y la conexión con la caja principal', icon: <IcoAparato size={18} />, onClick: () => navigate('/hub') },
          permisos.cerrarCaja && { label: 'Cerrar caja', hint: 'Corte del turno', danger: true, onClick: () => navigate('/close-session') },
          { label: 'Cerrar sesión', hint: permisos.cerrarCaja ? 'El turno sigue abierto' : undefined, danger: true, onClick: async () => {
            setMenuOpen(false);
            if (await pedirSalir({ titulo: '¿Cerrar sesión en este aparato?', texto: 'Las cuentas abiertas y el turno siguen guardados. Para volver a entrar se pide el correo y la contraseña.', confirmar: 'Cerrar sesión', peligro: true })) {
              cerrarSesion();
              navigate('/login', { replace: true });
            }
          } },
        ]}
      />
      {ConfirmarSalir}

      {/* ── Encabezado de teléfono: una fila ────────────────────────────── */}
      {telefono ? (
        <header className={styles.headerMovil}>
          {buscando ? (
            <div className={styles.buscarMovil}>
              <input
                ref={searchRef}
                className={`input ${styles.searchInput}`}
                type="search"
                placeholder="Buscar platillo…"
                value={search}
                autoFocus
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
              <button className={styles.hTexto} onClick={() => { setBuscando(false); setSearch(''); }}>Cancelar</button>
            </div>
          ) : (
            <>
              {esRestaurante && (
                <button className={styles.hBtn} onClick={() => navigate('/tables')} aria-label="Mapa de mesas">
                  <IcoTables size={20} />
                </button>
              )}
              <button className={styles.contexto} onClick={() => navigate(esRestaurante ? '/tables' : '/tabs')}>
                <strong>
                  {activeTab?.name ?? (permisos.comandera ? (waiter?.name ?? 'Comandera') : (terminal?.name ?? 'Terminal'))}
                </strong>
                <small>
                  {activeTab
                    ? [nComensales ? `${nComensales} comensal${nComensales !== 1 ? 'es' : ''}` : null, waiter?.name?.split(' ')[0] ?? null].filter(Boolean).join(' · ') || 'Cuenta abierta'
                    : 'Sin cuenta abierta'}
                </small>
              </button>
              <button className={styles.hBtn} onClick={() => setBuscando(true)} aria-label="Buscar">
                <IcoSearch size={20} />
              </button>
              <button className={styles.hBtn} onClick={() => setMenuOpen(true)} aria-label="Más opciones">
                <span className={styles.hPuntos}>⋯</span>
                {sinEnviar > 0 && <span className={styles.hDot} aria-hidden="true" />}
              </button>
            </>
          )}
          {scanFlash && (
            <div className={styles.scanFlash}><IcoCheck size={13} /> {scanFlash}</div>
          )}
        </header>
      ) : (
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <img src="/geek-commerce-dark.svg" alt="Geek Commerce" className={styles.logo} />
          <div className={styles.terminalInfo}>
            <span className={styles.terminalName}>
              {permisos.comandera ? (waiter?.name ?? 'Comandera') : (terminal?.name ?? 'Terminal')}
            </span>
            {/* El badge lleva a lo que no ha salido cuando hay algo esperando,
                y a la configuración del aparato cuando no. */}
            <button
              className="btn btn--ghost"
              style={{ padding: 0, minHeight: 0, background: 'none' }}
              onClick={() => navigate(sinEnviar > 0 ? '/pendientes' : '/hub')}
              aria-label={sinEnviar > 0 ? 'Ver lo que no ha salido' : 'Configurar este aparato'}
            >
              <SyncBadge />
            </button>
          </div>
          {/* Los totales del turno son del cajón, no del mesero. */}
          {!permisos.comandera && session?.sales_count > 0 && (
            <div className={styles.sessionStats}>
              <span className={styles.sessionSalesCount}>{session.sales_count} venta{session.sales_count !== 1 ? 's' : ''}</span>
              <span className={styles.sessionTotal}>
                ${Number(session.sales_total ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </span>
            </div>
          )}
        </div>
        <div className={styles.headerCenter}>
          <div className={styles.searchRow}>
            <input
              ref={searchRef}
              className={`input ${styles.searchInput}`}
              type="search"
              placeholder="Buscar producto o escanear código…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <button
              className={styles.numpadBtn}
              onClick={() => setNumpadOpen(true)}
              aria-label="Teclado numérico"
            >
              <IcoNumpad size={18} />
            </button>
          </div>
          {scanFlash && (
            <div className={styles.scanFlash}>
              <IcoCheck size={13} /> {scanFlash}
            </div>
          )}
        </div>
        {/* Lo que se toca cada minuto se queda a la vista; el resto va en "⋯".
            Eran ocho botones de 42 px que en una tablet de 8" se partían en
            tres filas. */}
        <div className={styles.headerRight}>
          <button
            className={`btn btn--ghost ${styles.tabsBtn}`}
            onClick={() => navigate('/tabs')}
          >
            <IcoTabs size={16} /> Cuentas{tabCount > 0 && <span className={styles.tabsBadge}>{tabCount}</span>}
          </button>
          {esRestaurante && (
            <button className={`btn btn--ghost ${styles.hIcono}`} aria-label="Mapa de mesas" onClick={() => navigate('/tables')}>
              <IcoTables size={20} />
            </button>
          )}
          {esRestaurante && (
            <button
              className={`btn btn--ghost ${styles.tabsBtn}`}
              aria-label={waiter ? `En turno: ${waiter.name}. Toca para cambiar.` : 'Identifícate con tu PIN'}
              onClick={() => setPinOpen(true)}
            >
              <IcoUser size={15} /> {waiter ? waiter.name.split(' ')[0] : 'Identifícate'}
            </button>
          )}
          {permisos.cerrarCaja && (
            <button className={`btn btn--ghost ${styles.cerrarCaja}`} onClick={() => navigate('/close-session')}>
              Cerrar caja
            </button>
          )}
          <button className={`btn btn--ghost ${styles.hIcono}`} aria-label="Más opciones" onClick={() => setMenuOpen(true)}>
            <span className={styles.hPuntos}>⋯</span>
            {sinEnviar > 0 && <span className={styles.hDot} aria-hidden="true" />}
          </button>
        </div>
      </header>
      )}

      {numpadOpen && (
        <NumpadModal
          onSearch={(code) => setSearch(code)}
          onClose={() => setNumpadOpen(false)}
        />
      )}

      {modifierFor && (
        <ModifierSheet
          product={modifierFor}
          groups={modifierFor.modifier_groups}
          onClose={() => setModifierFor(null)}
          onConfirm={(modifiers) => {
            addItem(modifierFor, modifiers, 1, 1, seatsActivos());
            setModifierFor(null);
          }}
        />
      )}

      {/* ── Main ───────────────────────────────────────────────────────── */}
      <div className={styles.main}>
        {/* Las categorías filtran el catálogo, no la cuenta: van sobre la
            columna de productos y el carrito sube hasta el encabezado. A todo
            lo ancho le quitaban 56 px a la lista de lo pedido, que en una
            tablet de 600 px de alto ya no cabía. */}
        <div className={styles.columna}>
          {categories.length > 0 && (
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeCategory === null ? styles.tabActive : ''}`}
                onClick={() => setActiveCategory(null)}
              >
                Todos
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`${styles.tab} ${activeCategory === cat ? styles.tabActive : ''}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Catálogo */}
          <section className={styles.catalog}>
            {loading ? (
              <div className={styles.loadingGrid}>
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className={styles.skeletonCard} />
                ))}
              </div>
            ) : loadErr ? (
              <div className={styles.empty}>
                <p>Error: {loadErr}</p>
                <button className="btn btn--ghost" style={{ marginTop: 12 }} onClick={loadProducts}>
                  Reintentar
                </button>
              </div>
            ) : products.length === 0 ? (
              <div className={styles.empty}>
                <p>
                  {search
                    ? `Sin resultados para "${search}"`
                    : activeCategory
                      ? `Sin productos en "${activeCategory}"`
                      : 'Sin productos disponibles'}
                </p>
              </div>
            ) : telefono ? (
              /* En teléfono la tarjeta con foto cuadrada medía 320×414: no cabía
                 un platillo completo en 360 px. Una fila de 60 px enseña siete. */
              <div className={styles.lista}>
                {!search && pinnedFull.length > 0 && (
                  <div className={styles.pinnedStrip}>
                    <span className={styles.pinnedLabel}><IcoStar size={13} filled /> Favoritos</span>
                    {pinnedFull.map((product) => (
                      <button key={product._id} className={styles.pinnedChip} onClick={() => requestAdd(product)}>
                        {product.name}
                        <span className={styles.pinnedPrice}>
                          ${Number(product.price ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {products.map((product) => {
                  const qty        = cartQty(product._id);
                  const outOfStock = product.stock != null && product.stock <= 0;
                  const overStock  = product.stock != null && qty > product.stock;
                  const sub = [
                    hasMods(product) ? 'con opciones' : null,
                    product.stock != null && (outOfStock ? 'Sin stock' : product.stock <= 5 ? `Quedan ${product.stock}` : null),
                  ].filter(Boolean).join(' · ');
                  return (
                    <div key={product._id} className={`${styles.fila} ${outOfStock ? styles.filaOut : ''}`}>
                      <button className={styles.filaMain} onClick={() => requestAdd(product)}>
                        {product.images?.[0] && <img src={product.images[0]} alt="" className={styles.filaImg} loading="lazy" decoding="async" />}
                        <span className={styles.filaTexto}>
                          <span className={styles.filaNombre}>{product.name}</span>
                          {sub && <span className={`${styles.filaSub} ${outOfStock || overStock ? styles.filaSubWarn : ''}`}>{sub}</span>}
                        </span>
                        <span className={styles.filaPrecio}>
                          ${product.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                        </span>
                      </button>
                      {qty > 0 && !hasMods(product) ? (
                        <div className={`${styles.filaQty} ${overStock ? styles.filaQtyOver : ''}`}>
                          <button className={styles.filaQtyBtn} aria-label="Uno menos" onClick={() => restarProducto(product._id, seatsActivos())}>−</button>
                          <span className={styles.filaQtyNum}>{qty}</span>
                          <button className={styles.filaQtyBtn} aria-label="Uno más" onClick={() => requestAdd(product)}>+</button>
                        </div>
                      ) : qty > 0 ? (
                        <span className={styles.filaBadge} aria-label={`${qty} en la cuenta`}>{qty}</span>
                      ) : (
                        <button className={styles.filaAdd} onClick={() => requestAdd(product)} aria-label={`Agregar ${product.name}`}>+</button>
                      )}
                    </div>
                  );
                })}
                {hasMore && (
                  <p className={styles.loadMoreHint} style={{ padding: '12px 16px' }}>Usa el buscador para encontrar más productos (mostrando 200).</p>
                )}
              </div>
            ) : (
              <div className={`${styles.grid} ${search ? styles.gridBuscando : ''}`}>
                {/* Fila de favoritos */}
                {!search && pinnedFull.length > 0 && (
                  <div className={styles.pinnedStrip}>
                    <span className={styles.pinnedLabel}><IcoStar size={13} filled /> Favoritos</span>
                    {pinnedFull.map((product) => (
                      <button
                        key={product._id}
                        className={styles.pinnedChip}
                        onClick={() => requestAdd(product)}
                      >
                        {product.name}
                        <span className={styles.pinnedPrice}>
                          ${Number(product.price ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {products.map((product) => {
                  const qty        = cartQty(product._id);
                  const outOfStock = product.stock != null && product.stock <= 0;
                  const overStock  = product.stock != null && qty > product.stock;
                  const isPinned   = pinned.some((p) => p._id === product._id);
                  return (
                    <div key={product._id} className={`${styles.productCard} ${outOfStock ? styles.productCardOut : ''}`}>
                      {/* Con modificadores el badge solo informa: no se puede
                          ajustar la cantidad desde aquí porque no sabemos a cuál
                          de las variantes pedidas se refiere. Se edita en el carrito. */}
                      {qty > 0 && hasMods(product) && (
                        <div className={`${styles.qtyBadge} ${overStock ? styles.qtyBadgeOver : ''}`}>
                          <span className={styles.qtyBadgeNum}>{qty}</span>
                        </div>
                      )}
                      {qty > 0 && !hasMods(product) && (
                        <div className={`${styles.qtyBadge} ${overStock ? styles.qtyBadgeOver : ''}`}>
                          <button
                            className={styles.qtyBadgeMinus}
                            onClick={(e) => { e.stopPropagation(); restarProducto(product._id, seatsActivos()); }}
                          >−</button>
                          {qtyPickerFor === product._id ? (
                            <input
                              className={styles.qtyBadgeInput}
                              type="number"
                              inputMode="numeric"
                              min="0"
                              value={qtyPickerVal}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setQtyPickerVal(e.target.value)}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') commitQtyPicker(product._id); if (e.key === 'Escape') setQtyPickerFor(null); }}
                              onBlur={() => commitQtyPicker(product._id)}
                            />
                          ) : (
                            <span
                              className={styles.qtyBadgeNum}
                              onClick={(e) => openQtyPicker(e, product._id, qty)}
                              role="button"
                              aria-label="Toca para cambiar cantidad"
                            >{qty}</span>
                          )}
                        </div>
                      )}
                      {/* Pin button */}
                      <button
                        className={`${styles.pinBtn} ${isPinned ? styles.pinBtnOn : ''}`}
                        onClick={(e) => togglePin(e, product)}
                        aria-label={isPinned ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                      >
                        <IcoStar size={14} filled={isPinned} />
                      </button>
                      <button
                        className={styles.productCardInner}
                        onClick={() => requestAdd(product)}
                      >
                        {product.images?.[0] && (
                          <img src={product.images[0]} alt="" className={styles.productImg} loading="lazy" decoding="async" />
                        )}
                        <div className={styles.productInfo}>
                          <div>
                            <p className={styles.productName}>{product.name}</p>
                            {product.sku && <p className={styles.productSku}>{product.sku}</p>}
                          </div>
                          <div className={styles.productPie}>
                            <p className={styles.productPrice}>
                              ${product.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                            {product.stock != null && (outOfStock || product.stock <= 5) && (
                              <p className={`${styles.productStock} ${outOfStock ? styles.stockOut : styles.stockLow}`}>
                                {outOfStock ? 'Sin stock' : `Quedan ${product.stock}`}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })}
                {hasMore && (
                  <div className={`${styles.productCard} ${styles.loadMoreCard}`}>
                    <p className={styles.loadMoreHint}>Usa el buscador para encontrar más productos (mostrando 200).</p>
                  </div>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Panel de carrito (tablet: columna a la derecha) */}
        {!telefono && <CartPanel />}
      </div>

      {/* Teléfono: la barra va al pie de la columna, en el flujo, no fija al
          viewport. Fija, en la PWA de iOS quedaba un hueco debajo después de
          usar el teclado del buscador. */}
      {telefono && <CartPanel />}

      <WaiterPinSheet open={pinOpen} onClose={() => setPinOpen(false)} />
    </div>
  );
}
